import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, GlobalStyles, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';

import Layout from './components/Layout';
import AIChatWidget from './components/AIChatWidget';
import HomePage from './pages/HomePage';
import VisionPage from './pages/VisionPage';
import SituationPage from './pages/SituationPage';
import ObjectivesPage from './pages/ObjectivesPage';
import ComponentsPage from './pages/ComponentsPage';
import ResourcesPage from './pages/ResourcesPage';
import TimelinePage from './pages/TimelinePage';
import RisksPage from './pages/RisksPage';
import GovernancePage from './pages/GovernancePage';
import PerformancePage from './pages/PerformancePage';
import AdaptationPage from './pages/AdaptationPage';
import DashboardPage from './pages/DashboardPage';
import SmartGoalsPage from './pages/SmartGoalsPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import useAuthStore from './store/authStore';
import useThemeStore from './store/themeStore';
import { createAppTheme, themeCssVars } from './theme';

/** Resolved colour mode, re-resolving when the OS preference changes. */
function useColorMode() {
  const preference = useThemeStore((state) => state.preference);
  const [systemDark, setSystemDark] = React.useState(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const mode = useColorMode();
  const theme = React.useMemo(() => createAppTheme(mode), [mode]);

  // Keep the document in sync so the browser paints the right scrollbars and
  // form controls, and so CSS can key off the mode if it ever needs to.
  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={themeCssVars(mode)} />
      <GlobalStyles
        styles={(t) => ({
          '::selection': {
            backgroundColor: alpha(t.palette.primary.main, 0.35),
            color: t.palette.mode === 'dark' ? t.palette.common.white : t.palette.common.black
          }
        })}
      />
      <Router>
        <Box sx={{ display: 'flex', minHeight: '100dvh', width: '100%', overflowX: 'hidden' }}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              isAuthenticated ? <Navigate to="/home" replace /> : <LoginPage />
            } />
            <Route path="/signup" element={
              isAuthenticated ? <Navigate to="/home" replace /> : <SignupPage />
            } />

            {/* Protected routes */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={
              <ProtectedRoute>
                <Layout><HomePage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/vision" element={
              <ProtectedRoute>
                <Layout><VisionPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/situation" element={
              <ProtectedRoute>
                <Layout><SituationPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/objectives" element={
              <ProtectedRoute>
                <Layout><ObjectivesPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/components" element={
              <ProtectedRoute>
                <Layout><ComponentsPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/resources" element={
              <ProtectedRoute>
                <Layout><ResourcesPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/timeline" element={
              <ProtectedRoute>
                <Layout><TimelinePage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/risks" element={
              <ProtectedRoute>
                <Layout><RisksPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/governance" element={
              <ProtectedRoute>
                <Layout><GovernancePage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/performance" element={
              <ProtectedRoute>
                <Layout><PerformancePage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/adaptation" element={
              <ProtectedRoute>
                <Layout><AdaptationPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout><DashboardPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/smart-goals" element={
              <ProtectedRoute>
                <Layout><SmartGoalsPage /></Layout>
              </ProtectedRoute>
            } />
          </Routes>
          {isAuthenticated && <AIChatWidget />}
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
